import { Box, Grid, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { useAnalytics } from 'use-analytics';
import { ReactComponent as AudienceOutlineIcon } from '../../../src/assets/audience-outline.svg';
import { ReactComponent as ShareIcon } from '../../../src/assets/share.svg';
import wevoIcon from '../../../src/assets/wevo-logo.png';
import { isAuthenticated } from '../../modules/user/helpers';
import ChatButton from './ChatButton';
import CustomButton from './ui/Button';

const ActionButtons = ({
  sessionId,
  segmentId,
  onSharePulse,
  onMyAudiencesClick,
  onRunStudy,
  isSessionOwner,
  hideChat,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMdAndUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgAndUp = useMediaQuery(theme.breakpoints.up('lg'));

  const { track } = useAnalytics();

  const getButtonStyles = () => {
    let minWidth;
    if (isLgAndUp) {
      minWidth = '170px';
    } else if (isMdAndUp) {
      minWidth = '100px';
    } else if (isSm) {
      minWidth = '140px';
    } else {
      minWidth = '0';
    }

    return {
      height: '40px',
      minWidth: minWidth,
      fontWeight: 400,
      border: '1px solid #D1CFCF',
      whiteSpace: 'nowrap',
      '&:hover': {
        borderColor: '#A0A0A0',
        opacity: 1,
      },
    };
  };

  const buttonStyles = getButtonStyles();

  const handleSharePulse = () => {
    track(TrackEvent.PULSE_CLICKED_ACTION_BAR_SHARE_RESULTS, { sessionId, segmentId });
    onSharePulse(true);
  };

  const handleNewPro = () => {
    onRunStudy({ trackEvent: TrackEvent.PULSE_CLICKED_ACTION_BAR_NEW_PRO });
  };

  const handleTryOtherPersonas = () => {
    track(TrackEvent.PULSE_CLICKED_ACTION_BAR_TRY_OTHER_PERSONAS, { sessionId, segmentId });
    onMyAudiencesClick();
  };

  return (
    <Grid
      container
      justifyContent="center"
      sx={{
        position: 'relative',
        paddingTop: hideChat ? 2 : 4,
        paddingBottom: 2,
        paddingX: 2,
        flexWrap: { lg: 'nowrap' },
        flexGrow: 1,
        columnGap: { xs: 2, md: 1, lg: 2 },
        backgroundColor: '#FFFFFF',
        borderBottomLeftRadius: '20px',
        borderBottomRightRadius: '20px',
      }}>
      <Grid item sx={{ order: { xs: 1, sm: 0 } }}>
        {isXs ? (
          <IconButton
            aria-label="share link"
            size="small"
            onClick={handleSharePulse}
            sx={{
              fontWeight: 400,
              border: '1px solid #D1CFCF',
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: '#A0A0A0',
                opacity: 1,
              },
            }}>
            <ShareIcon fill="#D1CFCF" />
          </IconButton>
        ) : (
          <CustomButton
            variant="secondaryDark"
            size="small"
            startIcon={<ShareIcon fill="#D1CFCF" style={{ marginRight: '-8px' }} />}
            onClick={handleSharePulse}
            sx={{
              ...buttonStyles,
              paddingLeft: 1,
              paddingRight: 2,
            }}>
            Share results
          </CustomButton>
        )}
      </Grid>
      {isSessionOwner && onMyAudiencesClick && (
        <Grid item>
          <CustomButton
            variant="secondaryDark"
            size="small"
            startIcon={<AudienceOutlineIcon fill="#D1CFCF" style={{ marginRight: '-8px' }} />}
            onClick={handleTryOtherPersonas}
            sx={{
              ...buttonStyles,
              paddingLeft: 1,
              paddingRight: 2,
            }}>
            Try other personas
          </CustomButton>
        </Grid>
      )}
      {isAuthenticated() && (
        <Grid item>
          {isXs ? (
            <IconButton
              aria-label="new pro"
              onClick={handleNewPro}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignitems: 'center',
                height: 40,
                width: 40,
                fontWeight: 400,
                border: '1px solid #D1CFCF',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: '#A0A0A0',
                  opacity: 1,
                },
              }}>
              <img src={wevoIcon} alt="WEVO logo" style={{ height: '14px', color: '#D1CFCF' }} />
            </IconButton>
          ) : (
            <CustomButton
              variant="secondaryDark"
              size="small"
              startIcon={
                <img
                  src={wevoIcon}
                  alt="WEVO logo"
                  style={{ height: '14px', color: '#D1CFCF', marginRight: '-4px' }}
                />
              }
              onClick={handleNewPro}
              sx={{
                ...buttonStyles,
                paddingX: 2,
              }}>
              New Pro
            </CustomButton>
          )}
        </Grid>
      )}
    </Grid>
  );
};

const actionButtonsBarStyles = {
  container: {
    position: 'relative',
    borderBottomLeftRadius: '20px',
    borderBottomRightRadius: '20px',
    backgroundColor: '#FFFFFF',
    border: 'none',
    boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.25)',
  },
  circle: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 60,
    height: 60,
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.25)',
  },
  iconButtonContainer: {
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
};

const ActionButtonsBar = ({
  sessionId,
  selectedSegment,
  onSharePulse,
  onMyAudiencesClick,
  onRunStudy,
  isSessionOwner,
  onOpenChat,
  hideActionButtons,
  hideChat,
  disableChat,
}) => {
  return (
    <Box sx={{ ...actionButtonsBarStyles.container, minHeight: hideChat ? 0 : 40 }}>
      {!hideChat && <div style={actionButtonsBarStyles.circle}></div>}
      {!hideActionButtons && (
        <ActionButtons
          sessionId={sessionId}
          segmentId={selectedSegment?.id}
          onSharePulse={onSharePulse}
          onMyAudiencesClick={onMyAudiencesClick}
          onRunStudy={onRunStudy}
          isSessionOwner={isSessionOwner}
          hideChat={hideChat}
        />
      )}
      {!hideChat && (
        <div style={actionButtonsBarStyles.iconButtonContainer}>
          <ChatButton onClick={() => onOpenChat(true)} disabled={disableChat} />
        </div>
      )}
    </Box>
  );
};

export default ActionButtonsBar;
